import type { LucideIcon } from 'lucide-react'

/** One "At a glance" / "Site details" fact. `sub` = lighter weight (mock `.gv.sub`). `subValue` adds a
 *  small muted second line (e.g. a time under a date). `pill` renders a status tag under the value
 *  (e.g. Overdue on the resolution-target cell). `icon` renders a small tile left of the text (the
 *  site-details cells); omit it for the plain text-only cell. Absent values are dropped by the caller. */
export interface GlanceFact {
  label: string
  value: string
  sub?: boolean
  subValue?: string
  pill?: { text: string; tone: 'red' | 'amber' | 'green' }
  icon?: LucideIcon
}

const PILL_TONE: Record<'red' | 'amber' | 'green', string> = {
  red: 'bg-gis-red-tint text-gis-red-d border-[#fecaca]',
  amber: 'bg-gis-amber-tint text-[#b45309] border-[#fed7aa]',
  green: 'bg-gis-green-tint text-gis-green border-[#bbf7d0]',
}

/** Label / value / optional sub-line + pill — the text stack of one cell, shared by both layouts. */
function FactText({ f }: { f: GlanceFact }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold tracking-[.05em] text-gis-subheading uppercase">{f.label}</div>
      <div className={`mt-[5px] text-[12.5px] leading-[1.35] ${f.sub ? 'font-medium text-gis-ink-2' : 'font-semibold text-gis-ink'}`}>
        {f.value}
      </div>
      {f.subValue && <div className="mt-[2px] text-[10.5px] font-medium text-gis-muted tabular-nums">{f.subValue}</div>}
      {f.pill && (
        <div className="mt-[6px]">
          <span className={`inline-flex items-center rounded-full border px-2 py-[1px] text-[10px] font-bold ${PILL_TONE[f.pill.tone]}`}>
            {f.pill.text}
          </span>
        </div>
      )}
    </div>
  )
}

/** 2-col facts grid (mock `.glance`). Renders nothing when there are no facts. */
export function GlanceGrid({ facts }: { facts: GlanceFact[] }) {
  if (facts.length === 0) {
    return null
  }
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-gis-line bg-gis-line">
      {facts.map((f) => (
        <div key={f.label} className="min-w-0 bg-white px-4 py-[10px]">
          {f.icon ? (
            <div className="flex items-start gap-[10px]">
              <span className="mt-[2px] grid h-[28px] w-[28px] flex-none place-items-center rounded-[8px] bg-gis-track text-gis-soft">
                <f.icon className="h-[14px] w-[14px] stroke-2" />
              </span>
              <FactText f={f} />
            </div>
          ) : (
            <FactText f={f} />
          )}
        </div>
      ))}
    </div>
  )
}
